/**
 * KPI Utils - 공통 유틸리티 함수
 *
 * @description 여러 store에서 공통으로 사용하는 헬퍼 함수 모음
 */
import type { KpiItem, KpiListResponseItem, KpiFormData, KpiTypeCd } from './useKpiTypes';

// ============================================
// KPI 매핑 헬퍼
// ============================================

/**
 * API 응답을 KpiItem 형식으로 변환
 */
export const mapKpiListItem = (item: KpiListResponseItem): KpiItem => {
  return {
    kpiId: item.kpiId || '',
    kpiNm: item.kpiNm || '',
    kpiTypeCd: (item.kpiTypeCd || '01') as KpiTypeCd,
    kpiOwnerType: item.kpiGbnId === '1' ? 'dept' : 'personal',
    weight: Number(item.weight) || 0,
    targetStr: item.targetStr || String(item.target || '') || '',
    actualStr: item.actualStr || String(item.actual || '') || '',
    accrueAchieve:
      Number(item.accrueAchieve) || Number(item.accumMethod) || Number(item.achive) || 0,
    lastMonthStatus: item.statusColor || '',
    // ⚠️ kpiList.do 응답에는 상태명이 없다 (색 statusColor 만 온다) — 여기서는 항상 ''.
    //    상태 텍스트가 필요한 화면(매니저 대시보드 KPI 그리드)은 상태 설정(kpiStatusList)의
    //    color ↔ statusNm 매핑으로 이름을 되찾는다. useManagerKpiStore.resolveStatusNm 참조.
    //    다른 엔드포인트가 이름을 함께 내려주는 경우를 대비해 필드는 남겨 둔다.
    statusNm: item.statusNm || '',
    plan: item.plan || '',
    shareGbn: item.shareGbn ?? undefined,
    sharingYn: item.sharingYn || (item.shareGbn === 'sharing' ? 'Y' : 'N'),
    sharedYn: item.sharedYn || (item.shareGbn === 'shared' ? 'Y' : 'N'),
    upKpiId: item.upKpiId || '',
    objRefGbnCd: item.objRefGbnCd || '',
    unit: item.unit || '',
    description: item.description || item.kpiDesc || '',
    accrueMethodCd: item.accrueMethodCd || '',
    rateMethodCd: item.rateMethodCd || '',
    evalTargetGbnCd: item.evalTargetGbnCd || '',
    upKpiNm: item.upKpiNm || '',
    kpiGbnId: item.kpiGbnId || '',
    orgKpiNm: item.orgKpiNm || '',
    managerKpiId: item.managerKpiId || '',
    managerKpiNm: item.managerKpiNm || '',
    refUserId: item.refUserId || '',
    refUserNm: item.refUserNm || '',
    _raw: item,
  };
};

// ============================================
// 상태 변환 헬퍼
// ============================================

/**
 * 월별실적 상태 변환 (statusColor -> 표시 라벨)
 */
export const getMonthlyStatusLabel = (statusColor: string): string => {
  if (statusColor === 'green' || statusColor === '#16a34a' || statusColor === '완료') return '완료';
  if (statusColor === 'blue' || statusColor === '#2563eb' || statusColor === '진행') return '진행';
  if (statusColor === 'yellow' || statusColor === '#ca8a04' || statusColor === '지연')
    return '지연';
  if (statusColor === 'red' || statusColor === '#dc2626' || statusColor === '보류') return '보류';
  return statusColor || '-';
};

/**
 * 월별실적 상태 변환 (statusCd -> 표시 라벨)
 */
export const getMonthlyStatusLabelByCode = (statusCd: string): string => {
  const statusMap: Record<string, string> = {
    '001': '완료',
    '002': '진행',
    '003': '지연',
    '004': '보류',
  };
  return statusMap[statusCd] || statusCd || '-';
};

/**
 * OKR 상태코드 매핑
 */
export const mapOkrStatus = (statusCd: string): 'progress' | 'complete' | 'pending' => {
  if (statusCd === '001' || statusCd === 'pending') return 'pending';
  if (statusCd === '003' || statusCd === 'complete') return 'complete';
  return 'progress';
};

/**
 * 업무 상태코드 매핑
 */
export const mapTaskStatus = (statusCd: string): 'progress' | 'complete' | 'plan' => {
  if (statusCd === '003' || statusCd === 'complete') return 'complete';
  if (statusCd === '001' || statusCd === 'plan') return 'plan';
  return 'progress';
};

/**
 * 상태 코드별 색상 반환
 */
export const getStatusColor = (statusCd: string): string => {
  switch (statusCd) {
    case 'S001':
      return '#3739e4'; // 우수 (파랑)
    case 'S002':
      return '#fec907'; // 보통 (노랑)
    case 'S003':
      return '#e02a10'; // 미흡 (빨강)
    default:
      return '#999';
  }
};

// ============================================
// 날짜 포맷 헬퍼
// ============================================

/**
 * YYYY-MM-DD → YYYYMMDD 변환
 */
export const formatDateForApi = (dateStr: string): string => {
  if (!dateStr) return '';
  return dateStr.replace(/[-./]/g, '');
};

/**
 * YYYYMMDD → YYYY-MM-DD 변환
 */
export const formatDateForDisplay = (dateStr: string): string => {
  if (!dateStr || dateStr.length !== 8) return dateStr;
  return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
};

/**
 * 월을 2자리 문자열로 변환 (1 → '01')
 */
export const padMonth = (month: string | number): string => {
  return String(month).padStart(2, '0');
};

// ============================================
// 배열 헬퍼
// ============================================

/**
 * 배열을 쉼표로 구분된 문자열로 변환
 */
export const arrayToCommaString = (arr: string[] | undefined): string => {
  return arr?.join(',') || '';
};

/**
 * 쉼표로 구분된 문자열을 배열로 변환
 */
export const commaStringToArray = (str: string | undefined): string[] => {
  if (!str) return [];
  return str.split(',').filter(Boolean);
};

// ============================================
// 폼 초기화
// ============================================

/**
 * KPI 폼 초기값 생성
 */
export const createDefaultKpiForm = () => ({
  kpiNm: '',
  upKpiId: '',
  kpiGbnId: 'personal' as const,
  orgKpiId: '',
  kpiTypeCd: '01' as const,
  accrueMethodCd: '001' as const,
  rateMethodCd: '001' as const,
  unit: '',
  description: '',
  weight: '',
  target: '',
  evalTargetGbnCd: '001' as const,
  linkType: '' as const,
  krIds: [] as string[],
  planIds: [] as string[],
  shareGbn: 'N',
  shareUserIds: [] as string[],
  copyYear: '',
  copyKpiId: '',
  refUserIds: [] as string[],
});

/**
 * OKR 폼 초기값 생성
 */
export const createDefaultOkrForm = () => ({
  krId: '',
  objId: '',
  objNm: '',
  krNm: '',
  startDt: '',
  endDt: '',
  content: '',
  evalTargetGbnCd: '001' as const,
  performMeasMtd: '001' as const,
  refUserIds: [] as string[],
  linkKpi: false,
  linkPlan: false,
  kpiIds: [] as string[],
  planIds: [] as string[],
  isNewObjective: false,
  cycleId: '',
  // KR 목록 (여러 KR 추가 가능)
  krList: [
    {
      krNm: '',
      startDt: '',
      endDt: '',
      content: '',
      evalTargetGbnCd: '001',
      performMeasMtd: '001',
      targetVal: '',
      planIds: [],
    },
  ] as Array<{
    krNm: string;
    startDt: string;
    endDt: string;
    content: string;
    evalTargetGbnCd: string;
    performMeasMtd: string;
    targetVal: string;
    planIds: string[];
  }>,
});

/**
 * 피드백 폼 초기값 생성
 */
export const createDefaultFeedbackForm = () => ({
  content: '',
  achive: '',
  statusCd: '',
  feedbackRatingId: '3',
  feedbackId: '',
});

// ============================================
// KPI 저장 데이터 빌더
// ============================================

interface BuildKpiSaveDataOptions {
  selectedYear: string;
  kpiId?: string;
  originalSharedYn?: string;
  originalUpKpiId?: string;
}

/**
 * KPI 등록/수정 공통 데이터 생성
 * @description saveNewGoal, updateKpi에서 중복되는 saveData 구성 로직 통합
 */
export const buildKpiSaveData = (kpiForm: KpiFormData, options: BuildKpiSaveDataOptions) => {
  const targetVal = kpiForm.kpiTypeCd === '02' ? '0' : kpiForm.target || '';
  const isOkrLink = kpiForm.linkType === 'okr';
  const isPlanLink = kpiForm.linkType === 'plan';
  const objRefGbnCdVal = isPlanLink ? '002' : '001';

  return {
    findYear: options.selectedYear,
    year: options.selectedYear,
    kpiId: options.kpiId || '',
    kpiNm: kpiForm.kpiNm,
    managerKpiId: kpiForm.upKpiId || '',
    upKpiId: options.originalUpKpiId || '',
    kpiGbnId: kpiForm.kpiGbnId === 'personal' ? '2' : '1',
    kpiTypeCd: kpiForm.kpiTypeCd || '01',
    objRefGbnCd: objRefGbnCdVal,
    unit: kpiForm.unit || '',
    basis: '',
    sharingYn: kpiForm.shareGbn === 'Y' ? 'Y' : 'N',
    sharedYn: options.originalSharedYn || 'N',
    description: kpiForm.description || '',
    weight: kpiForm.weight || '',
    plan: kpiForm.kpiTypeCd === '02' ? kpiForm.target : '',
    refUserIds: kpiForm.refUserIds?.join(',') || '',
    krIds: isOkrLink ? kpiForm.krIds?.join(',') || '' : '',
    planIds: isPlanLink ? kpiForm.planIds?.join(',') || '' : '',
    feedback: '.',
    orgKpiId: kpiForm.orgKpiId || '',
    accrueMethodCd: kpiForm.accrueMethodCd || '',
    rateMethodCd: kpiForm.rateMethodCd || '',
    evalTargetGbnCd: kpiForm.evalTargetGbnCd || '001',
    target: targetVal,
  };
};

// ============================================
// 분기(Cycle) 헬퍼
// ============================================

/**
 * 현재 월 기준 기본 분기 ID 결정
 * - fromMon ~ toMon 범위에 현재 월이 포함된 분기 선택
 * - 해당 분기가 없으면 목록의 첫 번째 분기 선택
 */
export const resolveDefaultCycleId = (cycles: { cycleId: string; fromMon?: string; toMon?: string }[]): string => {
  if (!cycles || cycles.length === 0) return '';

  const mon = new Date().getMonth() + 1;

  for (const cycle of cycles) {
    const fromMon = (cycle.fromMon || '').trim();
    const toMon = (cycle.toMon || '').trim();

    if (fromMon && toMon) {
      const from = parseInt(fromMon, 10);
      const to = parseInt(toMon, 10);
      if (!Number.isNaN(from) && !Number.isNaN(to) && from <= mon && to >= mon) {
        return cycle.cycleId;
      }
    }
  }

  return cycles[0].cycleId;
};
